import type {
  TColumnSearch,
  TRowSort,
  TUnderlyingDataExportDto,
  TUnderlyingDataSource as TUnderlyingDataSourceContract
} from '@homepal/api-contract'

import { TGlobalFilterApiInput } from 'types/GlobalGlobalFilter'
import { TMetricQueryParams, TMetricSettings } from 'types/GlobalMetric'
import { IUnderlyingDataResponse } from 'types/GlobalWidget'

import { getWithRedirect, postWithRedirect } from 'utils/axios'

const BASE_URL =
  process.env.NODE_ENV === 'development'
    ? 'http://localhost:3990/'
    : process.env.REACT_APP_HOMEPAL_MDM_API_PROD

const URL = `${BASE_URL}v1/underlying-data`
const EXPORTS_URL = `${URL}/exports`

/**
 * The source whose underlying data to fetch. Metric sources are not stored
 * entities, so their settings travel inline; the other types are resolved
 * server side from the stored entity. The wire structure is shared via the
 * contract; the metric settings shape is this side's own TMetricSettings.
 */
export type TUnderlyingDataSource =
  TUnderlyingDataSourceContract<TMetricSettings>

export type TUnderlyingDataOptions = {
  entityColumns?: string[]
  columnSearch?: TColumnSearch
  rowSort?: TRowSort
}

function toBody(
  source: TUnderlyingDataSource,
  queryParams: TMetricQueryParams,
  globalFilter: TGlobalFilterApiInput,
  options: TUnderlyingDataOptions
) {
  const { entityColumns, columnSearch, rowSort } = options

  return {
    data: {
      source,
      query_params: {
        ...(queryParams.filter ? { filter: queryParams.filter } : {}),
        ...(queryParams.from_date ? { from_date: queryParams.from_date } : {}),
        ...(queryParams.to_date ? { to_date: queryParams.to_date } : {}),
        ...(queryParams.period_filter
          ? { period_filter: queryParams.period_filter }
          : {}),
        ...(entityColumns ? { entity_columns: entityColumns } : {}),
        ...(columnSearch ? { column_search: columnSearch } : {}),
        ...(rowSort ? { row_sort: rowSort } : {}),
        global_filter: globalFilter
      }
    }
  }
}

export async function getUnderlyingData(
  source: TUnderlyingDataSource,
  queryParams: TMetricQueryParams,
  globalFilter: TGlobalFilterApiInput,
  options: TUnderlyingDataOptions = {}
) {
  return postWithRedirect<IUnderlyingDataResponse>(
    URL,
    toBody(source, queryParams, globalFilter, options)
  )
}

// Starts an export of every row to an Excel file on the server, downloaded
// under fileName.
export async function startUnderlyingDataExport(
  source: TUnderlyingDataSource,
  queryParams: TMetricQueryParams,
  globalFilter: TGlobalFilterApiInput,
  options: TUnderlyingDataOptions,
  fileName: string
) {
  const { data } = toBody(source, queryParams, globalFilter, options)

  return postWithRedirect<TUnderlyingDataExportDto>(EXPORTS_URL, {
    data: { ...data, file_name: fileName }
  })
}

export async function getUnderlyingDataExport(id: string) {
  return getWithRedirect<TUnderlyingDataExportDto>(`${EXPORTS_URL}/${id}`)
}
